import { apiRoutes } from "util/apiRoutes";

// Utility helpers to robustly manage document title and social metadata.
// All URLs and images are constructed using environment variables so that
// nothing is hardcoded.

type TwitterCardType = "summary" | "summary_large_image";
type OgType = "website" | "article" | "profile" | "video.other" | "object";

export interface OgMetaParams {
  title: string;
  description?: string;
  /** Absolute URL for the page; if not provided, uses buildAbsoluteUrl(urlPath) or window.location.href */
  url?: string;
  /** Optional path that will be joined with FE host to form an absolute URL */
  urlPath?: string;
  /** Absolute image URL for social previews */
  image?: string;
  /** Optional image alt text */
  imageAlt?: string;
  siteName?: string;
  locale?: string;
  ogType?: OgType;
  twitterCard?: TwitterCardType;
  twitterSite?: string;
  twitterCreator?: string;
}

const IMAGE_CDN: string = process.env.REACT_APP_IMAGE_CDN || "";

// Canonicalize all Societea subdomains to the primary www host for SEO
// This ensures dev/local hosts never become canonical in search engines.
const FE_HOSTNAME: string = (() => {
  const DEFAULT_CANONICAL_ORIGIN = "https://www.societea.app";
  const rawEnvHost: string = process.env.REACT_APP_FE_HOSTNAME || "";

  const normalizeOrigin = (value: string): string | null => {
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
  };

  const envOrigin = normalizeOrigin(rawEnvHost);
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : null;
  const candidate = envOrigin || currentOrigin || DEFAULT_CANONICAL_ORIGIN;

  try {
    const url = new URL(candidate);
    const host = url.hostname.toLowerCase();
    const isSocieteaDomain = /(^|\.)societea\.app$/i.test(host);
    if (isSocieteaDomain && host !== "www.societea.app") {
      return DEFAULT_CANONICAL_ORIGIN;
    }
    return url.origin;
  } catch {
    return DEFAULT_CANONICAL_ORIGIN;
  }
})();

// Small brand image used as the final fallback for social cards
// Provided path per product request; joined with configured CDN host at runtime
const DEFAULT_BRAND_IMAGE_PATH = "/soc_app_logo_dark.svg";

export const getDefaultBrandImage = (): string => {
  const trimmedCdn = IMAGE_CDN.replace(/\/$/, "");
  return `${trimmedCdn}${DEFAULT_BRAND_IMAGE_PATH}`;
};

export const buildAbsoluteUrl = (urlPath?: string): string => {
  if (typeof urlPath !== "string" || urlPath.length === 0) {
    return window.location.href;
  }
  const base = FE_HOSTNAME.endsWith("/") ? FE_HOSTNAME.slice(0, -1) : FE_HOSTNAME;
  return `${base}${urlPath.startsWith("/") ? urlPath : `/${urlPath}`}`;
};

export const cdnUrlFromKey = (key?: string | null): string | undefined => {
  if (!key || typeof key !== "string") return undefined;
  const trimmed = key.replace(/^\//, "");
  const cdn = IMAGE_CDN.replace(/\/$/, "");
  return `${cdn}/${trimmed}`;
};

const upsertMeta = (attr: "name" | "property", key: string, content: string): void => {
  if (typeof document === "undefined") return;
  const selector = `meta[${attr}='${key}']`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string): void => {
  const selector = `link[rel='${rel}']`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const setDocumentTitle = (title: string): void => {
  document.title = `${title}`;
};

/**
 * Sets Open Graph and Twitter metadata for the current page.
 * Values are strictly typed and sanitized. Absent values fall back to
 * sensible defaults to ensure previews always render something.
 */
export const setOgAndTwitterMeta = (params: OgMetaParams): void => {
  const siteName = params.siteName || "Societea";
  const url = params.url || buildAbsoluteUrl(params.urlPath);
  const image = params.image || getDefaultBrandImage();
  const imageAlt = params.imageAlt || siteName;
  const description = params.description || "Assess, prioritize, and address social issues .";
  const ogType: OgType = params.ogType || "website";
  const twitterCard: TwitterCardType = params.twitterCard || "summary_large_image";

  // Canonical link
  upsertLink("canonical", url);

  // Standard meta
  upsertMeta("name", "description", description);

  // Open Graph
  upsertMeta("property", "og:title", params.title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:type", ogType);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", image);
  upsertMeta("property", "og:image:alt", imageAlt);
  upsertMeta("property", "og:site_name", siteName);
  if (params.locale) upsertMeta("property", "og:locale", params.locale);

  // Twitter
  upsertMeta("name", "twitter:card", twitterCard);
  upsertMeta("name", "twitter:title", params.title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", image);
  if (params.twitterSite) upsertMeta("name", "twitter:site", params.twitterSite);
  if (params.twitterCreator) upsertMeta("name", "twitter:creator", params.twitterCreator);
};

/**
 * Convenience: set generic site-wide defaults for non-entity pages.
 */
export const setDefaultSiteOg = (overrides?: Partial<OgMetaParams>): void => {
  setOgAndTwitterMeta({
    title: overrides?.title || "Spill the tea on social issues",
    description: overrides?.description,
    url: overrides?.url,
    urlPath: overrides?.urlPath,
    image: overrides?.image || getDefaultBrandImage(),
    ogType: overrides?.ogType || "website",
    siteName: overrides?.siteName || "Societea",
    locale: overrides?.locale || "en_US",
    twitterCard: overrides?.twitterCard || "summary_large_image",
    twitterSite: overrides?.twitterSite || "@societea",
    twitterCreator: overrides?.twitterCreator,
    imageAlt: overrides?.imageAlt || "Societea logo",
  });
};

/**
 * Convenience to set both document title and default site OG/Twitter tags
 * based on the current location.
 */
export const setPageMeta = (title: string, description?: string): void => {
  setDocumentTitle(title);
  setDefaultSiteOg({
    title,
    description,
    url: window.location.href,
  });
};

/**
 * Lightweight helpers to derive a good social image for entity pages.
 * - For issues: prefer subject/reference thumbnail
 * - For other entities: prefer user's avatar; if media is available, prefer first image
 */
export interface GenericEntityLike {
  id?: string | number;
  title?: string;
  name?: string;
  username?: string | null;
  body?: string | null;
  description?: string | null;
  cname?: string;
  properties?: Record<string, unknown>;
  properties_public?: { avatar_key?: string } | null;
  url?: string | null;
  reference_link?: string | null;
}

const pickFirstImageFromMedia = (entity?: GenericEntityLike): string | undefined => {
  const files = (entity?.properties as { media_files?: Array<{ key: string; type: string; order?: number }> } | undefined)?.media_files;
  if (!files || files.length === 0) return undefined;
  const image = [...files]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .find((f) => f.type === "image");
  return cdnUrlFromKey(image?.key);
};

export const deriveIssueOgImage = (issue?: GenericEntityLike): string => {
  const byKey = cdnUrlFromKey((issue?.properties as { reference_link_img_key?: string } | undefined)?.reference_link_img_key);
  if (byKey) return byKey;
  const subjectThumb = (issue?.properties as { subject_properties?: { thumbnail?: { source?: string }; wiki_parse_first_img?: string } } | undefined)?.subject_properties;
  const source = subjectThumb?.thumbnail?.source || subjectThumb?.wiki_parse_first_img;
  if (source && /^https?:\/\//i.test(source)) return source;
  return getDefaultBrandImage();
};

export const deriveUserAvatarOgImage = (entity?: GenericEntityLike): string => {
  const avatarKey = entity?.properties_public?.avatar_key;
  const avatar = cdnUrlFromKey(typeof avatarKey === "string" ? avatarKey : undefined);
  return avatar || getDefaultBrandImage();
};

export const setEntityOg = (opts: {
  entityType: "issue" | "impact" | "solution" | "feedback" | "action" | "collection" | "influence" | "user" | "team" | "teabate";
  entity: GenericEntityLike | null | undefined;
  urlPath: string;
  explicitImageUrl?: string;
}): void => {
  const entity = opts.entity;
  const baseTitle = (() => {
    switch (opts.entityType) {
      case "issue":
        return entity?.name ? `Issue - ${String(entity.name).replace(/_/g, " ")}` : "Issue";
      case "impact":
        return entity?.title ? `Impact - ${entity.title}` : "Impact";
      case "solution":
        return entity?.title ? `Solution - ${entity.title}` : "Solution";
      case "feedback":
        return entity?.title ? `Feedback - ${entity.title}` : "Feedback";
      case "action":
        return entity?.title ? `Action - ${entity.title}` : "Action";
      case "collection":
        return entity?.title ? `Collection - ${entity.title}` : "Collection";
      case "influence":
        return entity?.title || "Influence";
      case "user":
        return entity?.username ? `User - ${entity.username}` : "User";
      case "team":
        return entity?.name ? `Partea - ${entity.name}` : "Partea";
      case "teabate":
        return entity?.title ? `${entity.title} | Teabate` : "Teabate";
      default:
        return "Societea";
    }
  })();

  const descriptionSource = entity?.body || entity?.description;
  const description = (descriptionSource && String(descriptionSource).trim().length > 0)
    ? String(descriptionSource).replace(/<[^>]+>/g, " ").slice(0, 300)
    : undefined;

  // Image strategy: Prefer precise image per entity type, else generate dynamic card
  let image: string | undefined = opts.explicitImageUrl;
  if (!image) {
    if (opts.entityType === "issue") {
      image = deriveIssueOgImage(entity || undefined);
    } else {
      image = pickFirstImageFromMedia(entity || undefined) || deriveUserAvatarOgImage(entity || undefined);
    }
  }

  // Compose dynamic OG card with counts and avatar if available
  try {
    const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    if (apiBase && entity) {
      const avatarKey = (entity as any)?.properties_public?.avatar_key as string | undefined;
      const avatar = cdnUrlFromKey(avatarKey);
      const username: string | undefined = (entity as any)?.username || (entity as any)?.teamname || undefined;
      const votes: number | undefined = (entity as any)?.vote_count || (entity as any)?.upvotes;
      const comments: number | undefined = (entity as any)?.comment_count || (entity as any)?.comments_count;
      const params = new URLSearchParams();
      params.set("title", baseTitle);
      if ((entity as any)?.name && opts.entityType === "issue") params.set("subtitle", String((entity as any).name));
      if (image) params.set("image", image);
      if (avatar) params.set("avatar", avatar);
      if (username) params.set("username", username);
      if (typeof votes === "number") params.set("votes", String(votes));
      if (typeof comments === "number") params.set("comments", String(comments));
      params.set("site", "Societea");
      image = `${apiBase}${apiRoutes.utilities.ogImage}?${params.toString()}`;
    }
  } catch {
    // fallback to prior image
  }

  setOgAndTwitterMeta({
    title: baseTitle,
    description,
    urlPath: opts.urlPath,
    image,
    ogType: opts.entityType === "issue" ? "article" : "article",
    siteName: "Societea",
    locale: "en_US",
    twitterCard: "summary_large_image",
    twitterSite: "@societea",
  });
};
